import { useEffect, useMemo, useState } from 'react';
import { apiErrorText, categoriesApi } from '@/shared/api';
import { ticketsApi } from '@/shared/api/tickets';
import type { Category } from '@/entities/category';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Field,
  Select,
  toast,
} from '@/shared/ui';
import styles from './ClassificationDialog.module.css';

const NO_SERVICE = '__none__';

export function ClassificationDialog({
  ticketId,
  open,
  categoryId,
  subcategoryId,
  onClose,
}: {
  ticketId: string;
  open: boolean;
  categoryId: string | null;
  subcategoryId: string | null;
  onClose: () => void;
}) {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [cat, setCat] = useState(categoryId ?? '');
  const [sub, setSub] = useState(subcategoryId ?? NO_SERVICE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCat(categoryId ?? '');
    setSub(subcategoryId ?? NO_SERVICE);
    categoriesApi
      .list()
      .then((res) => setCategories(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCategories([]));
  }, [open, categoryId, subcategoryId]);

  const services = useMemo(
    () => categories?.find((c) => c.id === cat)?.Subcategories ?? [],
    [categories, cat],
  );
  const unchanged = cat === (categoryId ?? '') && sub === (subcategoryId ?? NO_SERVICE);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cat || unchanged) return;
    setSaving(true);
    try {
      await ticketsApi.setClassification(ticketId, {
        categoryId: cat,
        subcategoryId: sub === NO_SERVICE ? null : sub,
      });
      toast.success('Категория заявки изменена');
      onClose();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось изменить категорию'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Категория и услуга</DialogTitle>
        <DialogDescription>
          Базовые очки KPI и сроки SLA пересчитаются по новой категории; уже нарушенный срок
          останется нарушенным. Группа, форма и согласования не меняются.
        </DialogDescription>
        <form onSubmit={handleSubmit} className={styles.form}>
          <Field label="Категория" required>
            <Select
              value={cat}
              onValueChange={(v) => {
                setCat(v);
                setSub(NO_SERVICE);
              }}
              options={(categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
              placeholder={categories ? 'Выберите категорию' : 'Загрузка…'}
              disabled={!categories}
            />
          </Field>
          <Field label="Услуга">
            <Select
              value={sub}
              onValueChange={setSub}
              options={[
                { value: NO_SERVICE, label: 'Без услуги' },
                ...services.map((s) => ({ value: s.id, label: s.name })),
              ]}
              disabled={!cat}
            />
          </Field>
          <DialogFooter>
            <DialogClose
              render={
                <Button variant="ghost" type="button">
                  Отмена
                </Button>
              }
            />
            <Button type="submit" disabled={saving || !cat || unchanged}>
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
